/*
  Life Admin — subscriptions/bills/renewals. Real CRUD page.
  Shows upcoming renewals, total monthly cost, per-item reminder toggle.
*/
import { useEffect, useMemo, useState } from "react";
import {
  Receipt, Plus, Trash2, Bell, BellOff, ExternalLink, Calendar, TrendingUp,
  AlertCircle, Check,
} from "lucide-react";
import { toast } from "sonner";
import { phase3Api } from "../lib/phase3Api";

const CADENCES = [
  ["weekly", "Weekly"],
  ["monthly", "Monthly"],
  ["quarterly", "Quarterly"],
  ["yearly", "Yearly"],
];

const CURRENCIES = ["GBP", "USD", "EUR", "AUD", "CAD", "INR"];

function fmtCurrency(pence, cur = "GBP") {
  const s = { GBP: "£", USD: "$", EUR: "€", AUD: "A$", CAD: "C$", INR: "₹" }[cur] || cur + " ";
  return `${s}${(pence / 100).toFixed(2)}`;
}

function daysUntil(iso) {
  try {
    const target = new Date(iso + "T00:00:00").getTime();
    const now = Date.now();
    return Math.ceil((target - now) / (1000 * 60 * 60 * 24));
  } catch { return null; }
}

function monthlyEquivalent(sub) {
  const p = Number(sub.amount_pence || 0);
  switch (sub.cadence) {
    case "weekly":    return p * 4.345;
    case "quarterly": return p / 3;
    case "yearly":    return p / 12;
    default:          return p;
  }
}

export default function LifeAdminPage() {
  const [subs, setSubs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [adding, setAdding] = useState(false);
  const [draft, setDraft] = useState({
    name: "", amount_pence: 0, currency: "GBP",
    renewal_date: "", cadence: "monthly",
    cancel_url: "", notes: "",
    reminder_days_before: 7, reminder_enabled: true,
  });

  async function load() {
    setLoading(true);
    try { setSubs(await phase3Api.listSubs()); }
    catch { /* noop */ }
    finally { setLoading(false); }
  }
  useEffect(() => { load(); }, []);

  async function submit(e) {
    e.preventDefault();
    if (!draft.name.trim() || !draft.renewal_date) { toast.error("Name + renewal date required"); return; }
    try {
      const created = await phase3Api.createSub({
        ...draft,
        amount_pence: Math.round(Number(draft.amount_pence) * 100),
      });
      setSubs(xs => [created, ...xs]);
      setAdding(false);
      setDraft({ name: "", amount_pence: 0, currency: "GBP", renewal_date: "", cadence: "monthly", cancel_url: "", notes: "", reminder_days_before: 7, reminder_enabled: true });
      toast.success("Added");
    } catch (e) { toast.error("Couldn't save"); }
  }

  async function toggleReminder(sub) {
    try {
      const updated = await phase3Api.updateSub(sub.id, { reminder_enabled: !sub.reminder_enabled });
      setSubs(xs => xs.map(s => s.id === sub.id ? updated : s));
    } catch { toast.error("Couldn't update"); }
  }

  async function removeSub(id) {
    try {
      await phase3Api.deleteSub(id);
      setSubs(xs => xs.filter(s => s.id !== id));
      toast.success("Removed");
    } catch { toast.error("Couldn't remove"); }
  }

  const monthlyTotal = useMemo(() => subs.reduce((acc, s) => acc + monthlyEquivalent(s), 0), [subs]);
  const soonest = useMemo(() => [...subs].sort((a, b) => (daysUntil(a.renewal_date) ?? 1e9) - (daysUntil(b.renewal_date) ?? 1e9))[0], [subs]);

  return (
    <div className="animate-fade-up max-w-3xl">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <span className="label-caps">Subs · bills · renewals</span>
          <h1 className="mt-2 text-3xl sm:text-4xl lg:text-5xl font-display font-black tracking-tighter">Life admin.</h1>
          <p className="mt-2 text-sm sm:text-base" style={{ color: "var(--text-muted)" }}>
            One card per service. Get reminded before each renewal so nothing silently renews on you.
          </p>
        </div>
        <button onClick={() => setAdding(a => !a)}
          data-testid="la-add"
          className="inline-flex items-center gap-2 px-3 h-10 rounded-lg text-sm font-semibold text-white shrink-0"
          style={{ background: "var(--primary)" }}>
          <Plus className="h-4 w-4" /> Add
        </button>
      </div>

      {/* summary tiles */}
      {subs.length > 0 && (
        <div className="grid grid-cols-2 gap-3 mb-6" data-testid="la-summary">
          <div className="card-tactile rounded-xl p-4">
            <div className="label-caps text-[10px] inline-flex items-center gap-1"><TrendingUp className="h-3 w-3" /> Monthly equivalent</div>
            <div className="font-display font-black text-2xl mt-1">{fmtCurrency(Math.round(monthlyTotal), subs[0]?.currency || "GBP")}</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>Across {subs.length} active</div>
          </div>
          <div className="card-tactile rounded-xl p-4">
            <div className="label-caps text-[10px] inline-flex items-center gap-1"><Calendar className="h-3 w-3" /> Next renewal</div>
            <div className="font-display font-black text-2xl mt-1 truncate">{soonest?.name}</div>
            <div className="text-[11px]" style={{ color: "var(--text-muted)" }}>
              {(() => { const d = daysUntil(soonest?.renewal_date); return d === null ? "—" : d <= 0 ? "today" : `in ${d} day${d === 1 ? "" : "s"}`; })()}
            </div>
          </div>
        </div>
      )}

      {/* add form */}
      {adding && (
        <form onSubmit={submit} className="card-tactile rounded-2xl p-5 mb-6 space-y-3" data-testid="la-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <label><div className="label-caps mb-1">Name</div>
              <input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })}
                data-testid="la-name" required
                placeholder="Netflix"
                className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] focus:outline-none focus:border-[color:var(--primary)]"
                style={{ borderColor: "var(--border-subtle)" }} /></label>
            <label><div className="label-caps mb-1">Amount</div>
              <div className="flex gap-2">
                <input type="number" step="0.01" min="0" value={draft.amount_pence}
                  onChange={(e) => setDraft({ ...draft, amount_pence: e.target.value })}
                  data-testid="la-amount"
                  className="flex-1 px-3 py-2 rounded-md border bg-[color:var(--surface)]"
                  style={{ borderColor: "var(--border-subtle)" }} />
                <select value={draft.currency} onChange={(e) => setDraft({ ...draft, currency: e.target.value })}
                  className="px-2 py-2 rounded-md border bg-[color:var(--surface)] text-sm"
                  style={{ borderColor: "var(--border-subtle)" }}>
                  {CURRENCIES.map(c => <option key={c}>{c}</option>)}
                </select>
              </div></label>
            <label><div className="label-caps mb-1">Next renewal</div>
              <input type="date" value={draft.renewal_date} onChange={(e) => setDraft({ ...draft, renewal_date: e.target.value })}
                data-testid="la-date" required
                className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)]"
                style={{ borderColor: "var(--border-subtle)" }} /></label>
            <label><div className="label-caps mb-1">Cadence</div>
              <select value={draft.cadence} onChange={(e) => setDraft({ ...draft, cadence: e.target.value })}
                className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)]"
                style={{ borderColor: "var(--border-subtle)" }}>
                {CADENCES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select></label>
            <label className="sm:col-span-2"><div className="label-caps mb-1">Cancel link (optional)</div>
              <input type="url" value={draft.cancel_url} onChange={(e) => setDraft({ ...draft, cancel_url: e.target.value })}
                placeholder="https://netflix.com/CancelPlan"
                className="w-full px-3 py-2 rounded-md border bg-[color:var(--surface)] text-sm"
                style={{ borderColor: "var(--border-subtle)" }} /></label>
            <label><div className="label-caps mb-1">Remind me this many days before</div>
              <input type="number" min={0} max={90} value={draft.reminder_days_before}
                onChange={(e) => setDraft({ ...draft, reminder_days_before: Number(e.target.value) || 0 })}
                className="w-24 px-3 py-2 rounded-md border bg-[color:var(--surface)]"
                style={{ borderColor: "var(--border-subtle)" }} /></label>
          </div>
          <div className="flex gap-2 pt-2">
            <button type="submit" data-testid="la-submit"
              className="inline-flex items-center gap-2 px-4 h-10 rounded-lg text-sm font-semibold text-white"
              style={{ background: "var(--primary)" }}><Check className="h-4 w-4" /> Save</button>
            <button type="button" onClick={() => setAdding(false)}
              className="px-4 h-10 rounded-lg text-sm font-semibold border hover:bg-[color:var(--surface-2)]"
              style={{ borderColor: "var(--border-subtle)" }}>Cancel</button>
          </div>
        </form>
      )}

      {/* list */}
      {loading ? (
        <div className="text-sm py-8 text-center" style={{ color: "var(--text-muted)" }}>Loading…</div>
      ) : subs.length === 0 ? (
        <div className="card-tactile rounded-2xl p-8 text-center">
          <Receipt className="h-8 w-8 mx-auto mb-2" style={{ color: "var(--text-muted)" }} />
          <p className="text-sm" style={{ color: "var(--text-muted)" }}>Nothing tracked yet. Add your first subscription above.</p>
        </div>
      ) : (
        <ul className="space-y-2" data-testid="la-list">
          {subs.map(s => {
            const days = daysUntil(s.renewal_date);
            const soon = days !== null && days <= s.reminder_days_before;
            return (
              <li key={s.id} className="card-tactile rounded-xl p-4 flex items-start gap-3" data-testid="la-row">
                <div className="h-9 w-9 rounded-lg shrink-0 bg-[color:var(--primary-soft)] flex items-center justify-center">
                  <Receipt className="h-4 w-4 text-[color:var(--primary-ink)]" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2">
                    <div className="font-display font-bold truncate">{s.name}</div>
                    {soon && <span className="text-[10px] inline-flex items-center gap-1" style={{ color: "var(--accent-ink)" }}>
                      <AlertCircle className="h-3 w-3" /> soon
                    </span>}
                  </div>
                  <div className="text-xs mt-0.5 flex flex-wrap items-center gap-2" style={{ color: "var(--text-muted)" }}>
                    <span>{fmtCurrency(s.amount_pence, s.currency)} / {CADENCES.find(c => c[0] === s.cadence)?.[1].toLowerCase()}</span>
                    <span>·</span>
                    <span>{s.renewal_date}{days !== null && days >= 0 ? ` (${days === 0 ? "today" : `${days}d`})` : ""}</span>
                  </div>
                  {s.cancel_url && <a href={s.cancel_url} target="_blank" rel="noreferrer"
                    className="text-[11px] mt-1 inline-flex items-center gap-1 hover:underline"
                    style={{ color: "var(--primary-ink)" }}>
                    Cancel link <ExternalLink className="h-3 w-3" />
                  </a>}
                </div>
                <button onClick={() => toggleReminder(s)} title={s.reminder_enabled ? "Reminder on" : "Reminder off"}
                  data-testid={`la-bell-${s.id}`}
                  className="p-1.5 rounded hover:bg-[color:var(--surface-2)] shrink-0">
                  {s.reminder_enabled
                    ? <Bell className="h-4 w-4" style={{ color: "var(--primary-ink)" }} />
                    : <BellOff className="h-4 w-4" style={{ color: "var(--text-muted)" }} />}
                </button>
                <button onClick={() => removeSub(s.id)} className="p-1.5 rounded hover:bg-[color:var(--surface-2)] shrink-0">
                  <Trash2 className="h-4 w-4" style={{ color: "var(--text-muted)" }} />
                </button>
              </li>
            );
          })}
        </ul>
      )}

      <div className="mt-8 pt-6 border-t text-xs" style={{ borderColor: "var(--border-subtle)", color: "var(--text-muted)" }}>
        Reminders use your existing VeriHandi push notifications. Enable notifications in Settings → Notifications to receive them.
      </div>
    </div>
  );
}
